import React from 'react';

import { cn, AgentEmptyEntrance } from '@modernloop/ai-elements';
import { useTheme } from '@mui/material/styles';

type Props = {
  /** True while the thread holds no messages. */
  isEmpty: boolean;
};

/**
 * Lotus mesh gradient behind the empty Agent canvas.
 *
 * The mesh and its fade read straight from the MUI palette, so the fade stays an inline style
 * rather than a utility class: the fade colour is whatever the active theme calls its default
 * surface. The element stays mounted across both states, so the state change alone drives the
 * CSS transition and no effect is needed to animate it.
 *
 * The outer box never moves and clips. It is the only thing that hides the mesh: the inner layer
 * travels a full layer height upward at unchanged opacity until the clip has swallowed all of it,
 * so the rising mesh can never paint outside these bounds and over the opaque header above them.
 * The box also sits on a negative layer, which keeps it behind the transcript without giving any
 * sibling a z-index that would cover the composer.
 */
const AgentEmptyGradient: React.FC<Props> = ({ isEmpty }) => {
  const theme = useTheme();
  const image = theme.palette.mode === 'dark' ? 'dark' : 'light';

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-10 h-[90vh] overflow-hidden"
      data-testid="agent-empty-gradient"
    >
      <div
        className={cn(
          'absolute inset-0',
          // Tailwind v4 moves elements with the `translate` property, not `transform`, so the
          // transition must name `translate` or the rise snaps instead of animating.
          'transition-[translate] duration-2000 ease-out motion-reduce:transition-none',
          // Only the zero-message canvas shows the gradient. The first message travels it a full
          // layer height upward and the stationary wrapper clips it: the mesh keeps its own
          // opacity the whole way out, so it slides off the canvas rather than dissolving on it.
          isEmpty ? 'translate-y-0' : '-translate-y-full'
        )}
        data-testid="agent-empty-gradient-layer"
      >
        <AgentEmptyEntrance.Mesh
          alt=""
          decoding="async"
          height="1000"
          src={`/images/mesh-gradient-square-${image}.png`}
          style={{
            height: '100%',
            left: 0,
            opacity: 0.25,
            position: 'absolute',
            top: 0,
            width: '100%',
          }}
          width="1000"
        />
        <div
          data-testid="agent-empty-gradient-fade"
          style={{
            backgroundImage: `linear-gradient(to top, ${theme.palette.background.default} 25%, transparent)`,
            bottom: 0,
            height: '100%',
            left: 0,
            position: 'absolute',
            right: 0,
          }}
        />
      </div>
    </div>
  );
};

export default AgentEmptyGradient;
